import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Bike, Package, Clock, ExternalLink, Sparkles } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

declare global {
  interface Window {
    FlareSearch?: {
      init: (config: FlareSearchConfig) => void
      destroy: () => void
    }
  }
}

interface FlareSearchConfig {
  inputSelector: string
  apiBaseUrl: string
  mode: 'dropdown' | 'fullscreen'
  theme?: {
    primaryColor?: string
    backgroundColor?: string
    textColor?: string
    borderRadius?: string
  }
  debounceMs?: number
  minChars?: number
  maxResults?: number
}

interface StoreInfo {
  store: {
    name: string
    productCount: number
    lastSync: string | null
  }
  models?: Array<{ name: string; fitmentCount: number }>
  typeCounts?: Record<string, number>
  popularSearches?: string[]
}

// Fallback example queries if popular searches haven't loaded yet.
const FALLBACK_QUERIES = [
  '1971 Norton Commando',
  'BSA clutch plate',
  'Triumph T140 oil tank',
  '061073',
  'Norton carburettor',
  'timing cover gasket',
]

export function LandingPage() {
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false)
  const [store, setStore] = useState<StoreInfo | null>(null)
  const scriptRef = useRef<HTMLScriptElement | null>(null)

  // Fetch store info
  useEffect(() => {
    fetch('/api/store/info')
      .then((r) => (r.ok ? (r.json() as Promise<StoreInfo>) : null))
      .then((d) => {
        if (d) setStore(d)
      })
      .catch(() => {})
  }, [])

  // Load widget
  useEffect(() => {
    if (document.querySelector('script[src="/widget.js"]')) {
      setIsWidgetLoaded(true)
      return
    }
    const script = document.createElement('script')
    script.src = '/widget.js'
    script.async = true
    script.onload = () => setIsWidgetLoaded(true)
    script.onerror = () => console.error('Failed to load widget.js')
    document.body.appendChild(script)
    scriptRef.current = script
    return () => {
      window.FlareSearch?.destroy()
    }
  }, [])

  useEffect(() => {
    if (!isWidgetLoaded || !window.FlareSearch) return
    const timer = setTimeout(() => {
      window.FlareSearch?.destroy()
      window.FlareSearch?.init({
        inputSelector: '#hero-search',
        apiBaseUrl: window.location.origin,
        mode: 'fullscreen',
        theme: {
          primaryColor: '#2563eb',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          borderRadius: '12px',
        },
        debounceMs: 120,
        minChars: 2,
        maxResults: 12,
      })
    }, 100)
    return () => clearTimeout(timer)
  }, [isWidgetLoaded])

  const setQuery = (q: string) => {
    const el = document.getElementById('hero-search') as HTMLInputElement | null
    if (!el) return
    el.focus()
    el.value = q
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }

  const productCount = store?.store.productCount ?? 0
  const fitmentCount = store?.typeCounts?.['fitment'] ?? 0
  const modelCount = store?.models?.length ?? 0
  const lastSyncDisplay = store?.store.lastSync
    ? formatDistanceToNow(new Date(store.store.lastSync), { addSuffix: true })
    : '—'

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Top bar */}
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <img
              src="/cbs-logo.png"
              alt="Classic Bike Spares"
              className="h-9 w-auto"
              width={124}
              height={36}
            />
            <span className="text-xs text-muted-foreground hidden sm:inline border-l border-border pl-3">
              Search
            </span>
          </div>
          <nav className="flex items-center gap-5 text-sm">
            <a
              href="https://classicbikespares.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
            >
              Main site
              <ExternalLink className="h-3 w-3" />
            </a>
            <Link
              to="/sign-in"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Admin
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-5 pt-16 pb-20 text-center">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Find parts for your classic bike.
          </h1>
          <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
            Search {productCount.toLocaleString()} parts across {fitmentCount} bike-specific
            fitment guides. Type a bike, a part name, or an OEM number.
          </p>

          {/* Search */}
          <div className="mt-10 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              id="hero-search"
              type="search"
              placeholder="Try: 1971 Norton Commando, timing cover, 061073..."
              className="pl-12 pr-4 h-14 text-base rounded-xl border-2 focus-visible:ring-primary/20 focus-visible:ring-4"
              autoComplete="off"
              autoFocus
            />
          </div>

          {/* Example queries — prefer real popular searches, fall back to defaults */}
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {(store?.popularSearches && store.popularSearches.length >= 3
              ? store.popularSearches.slice(0, 6)
              : FALLBACK_QUERIES
            ).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setQuery(q)}
                className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted/40 hover:bg-muted hover:border-primary/40 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </section>

        {/* Stats strip */}
        <section className="border-y border-border bg-muted/20">
          <div className="max-w-4xl mx-auto px-5 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat
              icon={<Package className="h-4 w-4" />}
              label="Products"
              value={productCount ? productCount.toLocaleString() : '—'}
            />
            <Stat
              icon={<Bike className="h-4 w-4" />}
              label="Bike fitments"
              value={fitmentCount ? fitmentCount.toLocaleString() : '—'}
            />
            <Stat
              icon={<Sparkles className="h-4 w-4" />}
              label="Bike models"
              value={modelCount ? String(modelCount) : '—'}
            />
            <Stat
              icon={<Clock className="h-4 w-4" />}
              label="Last sync"
              value={lastSyncDisplay}
              small
            />
          </div>
        </section>

        {/* Bike models */}
        {store?.models && store.models.length > 0 && (
          <section className="max-w-4xl mx-auto px-5 py-16">
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4 text-center">
              Fitments available for these bikes
            </h2>
            <div className="flex flex-wrap justify-center gap-2">
              {store.models.map((m) => (
                <button
                  key={m.name}
                  type="button"
                  onClick={() => setQuery(m.name)}
                  className="text-sm px-3 py-1.5 rounded-md bg-card border border-border hover:border-primary/40 hover:bg-muted/60 transition-colors"
                >
                  {m.name}
                  <span className="text-muted-foreground ml-2 text-xs">
                    ({m.fitmentCount})
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* How it works */}
        <section className="max-w-3xl mx-auto px-5 py-16 text-center">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-6">
            How to find parts
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <HowCard
              n="1"
              title="Search by bike"
              body={'Type your bike (e.g. "1971 Norton Commando") to see every fitment guide for that model.'}
            />
            <HowCard
              n="2"
              title="Browse the parts"
              body="Each fitment opens a diagrammed parts list with live prices and stock status."
            />
            <HowCard
              n="3"
              title="Or search by OEM"
              body={'Know the part number? Paste it (e.g. "061073") to jump straight to the product and every fitment it belongs to.'}
            />
          </div>
          <div className="mt-10">
            <Button asChild variant="default" size="lg">
              <a
                href="https://classicbikespares.com.au"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit classicbikespares.com.au
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-6">
        <div className="max-w-5xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>Search powered by ShopFlare on Cloudflare&rsquo;s edge.</div>
          <div>
            <Link to="/sign-in" className="hover:text-foreground transition-colors">
              Admin sign-in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

function Stat({
  icon,
  label,
  value,
  small,
}: {
  icon: React.ReactNode
  label: string
  value: string
  small?: boolean
}) {
  return (
    <div className="text-center md:text-left">
      <div className="flex items-center justify-center md:justify-start gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className={small ? 'text-sm font-medium mt-1.5' : 'text-2xl font-semibold mt-1'}>
        {value}
      </div>
    </div>
  )
}

function HowCard({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border p-5 bg-card">
      <div className="text-xs font-semibold text-primary mb-2">Step {n}</div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  )
}
